/**
 * Settings Page – the signed-in user's profile and how they sign in
 */
import type { ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { KeyRound, Loader2 } from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { useTranslations } from "@/hooks/use-translations";
import { authClient } from "@/lib/auth-client";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";

const ROLE_KEYS: Record<string, string> = {
	user: "roleUser",
	admin: "roleAdmin",
	super_admin: "roleSuperAdmin",
};

function useFormatDate() {
	const { i18n } = useTranslation();
	const lang = i18n.resolvedLanguage ?? i18n.language;
	return (value: Date | string) =>
		new Date(value).toLocaleDateString(lang, {
			day: "numeric",
			month: "long",
			year: "numeric",
		});
}

function InfoRow({ label, children }: { label: string; children: ReactNode }) {
	return (
		<div className="min-w-0">
			<dt className="text-xs text-text-tertiary">{label}</dt>
			<dd className="mt-1 flex items-center gap-2 text-sm text-text-primary">
				{children}
			</dd>
		</div>
	);
}

function ProfileCard() {
	const t = useTranslations("settings");
	const formatDate = useFormatDate();
	const { user } = useAuth();

	if (!user) return null;

	const notSet = <span className="text-text-tertiary">{t("notSet")}</span>;
	const roleKey = user.siteRole ? ROLE_KEYS[user.siteRole] : undefined;

	return (
		<Card>
			<div className="flex items-center gap-4 p-5">
				{user.image ? (
					<img
						src={user.image}
						alt=""
						className="size-12 shrink-0 rounded-full object-cover"
					/>
				) : (
					<div className="flex size-12 shrink-0 items-center justify-center rounded-full bg-brand-100 text-base font-semibold text-text-primary">
						{user.name.trim().charAt(0).toUpperCase() || "?"}
					</div>
				)}
				<div className="min-w-0">
					<h2 className="truncate text-base font-semibold text-text-primary">
						{user.name}
					</h2>
					<p className="truncate text-sm text-text-tertiary">{user.email}</p>
				</div>
			</div>

			<dl className="grid gap-x-6 gap-y-4 border-t border-border-subtle p-5 sm:grid-cols-2">
				<InfoRow label={t("email")}>
					<span className="truncate">{user.email}</span>
					<Badge tone={user.emailVerified ? "success" : "warning"}>
						{t(user.emailVerified ? "emailVerified" : "emailNotVerified")}
					</Badge>
				</InfoRow>
				<InfoRow label={t("phone")}>{user.phone || notSet}</InfoRow>
				<InfoRow label={t("telegram")}>{user.telegramId || notSet}</InfoRow>
				<InfoRow label={t("role")}>
					{roleKey ? t(roleKey) : user.siteRole || notSet}
				</InfoRow>
				<InfoRow label={t("memberSince")}>{formatDate(user.createdAt)}</InfoRow>
			</dl>
		</Card>
	);
}

function SignInMethodsCard() {
	const t = useTranslations("settings");
	const formatDate = useFormatDate();

	// Rows of the `account` table for this user; better-auth leaves out tokens
	// and the password hash.
	const { data: accounts, isLoading, isError } = useQuery({
		queryKey: ["authAccounts"],
		queryFn: async () => {
			const { data, error } = await authClient.listAccounts();
			if (error) throw new Error(error.message);
			return data;
		},
	});

	return (
		<Card>
			<div className="p-5 pb-0">
				<h2 className="text-base font-semibold text-text-primary">
					{t("signInMethods")}
				</h2>
				<p className="text-sm text-text-tertiary">{t("signInMethodsDescription")}</p>
			</div>
			<div className="p-5">
				{isLoading ? (
					<Loader2 size={18} className="animate-spin text-text-tertiary" />
				) : isError ? (
					<p className="text-sm text-danger">{t("accountsLoadError")}</p>
				) : !accounts?.length ? (
					<p className="text-sm text-text-tertiary">{t("noAccounts")}</p>
				) : (
					<ul className="space-y-2">
						{accounts.map((account) => (
							<li
								key={account.id}
								className="flex items-center gap-3 rounded-xl border border-border-subtle p-3"
							>
								<div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-brand-100">
									<KeyRound size={18} className="text-text-primary" />
								</div>
								<div className="min-w-0">
									<p className="text-sm font-medium text-text-primary">
										{account.providerId === "credential"
											? t("providerCredential")
											: account.providerId}
									</p>
									<p className="text-xs text-text-tertiary">
										{t("accountAdded", { date: formatDate(account.createdAt) })}
									</p>
								</div>
							</li>
						))}
					</ul>
				)}
			</div>
		</Card>
	);
}

export function SettingsPage() {
	const t = useTranslations("settings");

	return (
		<div className="min-h-screen p-6 bg-bg-canvas">
			<div className="max-w-7xl mx-auto">
				<div className="mb-8">
					<h1 className="text-2xl font-semibold text-text-primary">{t("title")}</h1>
					<p className="text-text-tertiary">{t("description")}</p>
				</div>

				<div className="space-y-4">
					<ProfileCard />
					<SignInMethodsCard />
				</div>
			</div>
		</div>
	);
}
